import { Key, ReactNode, SyntheticEvent, useMemo, useState } from "react";
import { useOutSideClick } from "src/hooks/outSideClick";
import classnames from 'classnames';

import { IDropdownOption } from "../../../constants/interfaces";
import './style.scss';
import { useTranslation } from "src/hooks/translation";

interface IProps<TValue> {
    value?: TValue;
    placeholder?: string | ReactNode;
    options: IDropdownOption<string | ReactNode, TValue>[];
    showSelected?: boolean;
    showArrow?: boolean;
    className?: string;
    onChange?(selected: IDropdownOption<string | ReactNode, TValue>): void;
    onToggleOpen?(open: boolean): void;
    classic?: boolean;
}

const Select = <TValue extends unknown>({
    value,
    className,
    showSelected = true,
    showArrow = true,
    placeholder,
    options,
    onChange,
    onToggleOpen,
    classic,
}: IProps<TValue>) => {
    const selectRef = useOutSideClick(() => {
        setOpenDirection(null);
        onToggleOpen?.(false);
    }, [onToggleOpen]);

    const [openDirection, setOpenDirection] = useState<string | null>();
    const t = useTranslation();

    const toggleOpen = (e: SyntheticEvent) => {
        e.stopPropagation();

        if (openDirection) {
            onToggleOpen?.(false);
            return setOpenDirection(null);
        }

        const selectTop = selectRef.current?.getBoundingClientRect().top || 0;
        const selectHeight = selectRef.current?.offsetHeight || 0

        const direction = window.innerHeight - selectTop - selectHeight < 300 ? 'up' : 'down';

        setOpenDirection(direction);
        onToggleOpen?.(true);
    }

    const selected = useMemo(() => (
        (value || value === 0)
            ? options.find(item => item.value === value)
            : null
    ), [value, options]);

    const select = (e: SyntheticEvent, option: IDropdownOption<string | ReactNode, TValue>) => {
        e.stopPropagation();
        onChange?.(option);
        setOpenDirection(null);
        onToggleOpen?.(false);
    };

    const containerClassName = classnames('B-betslip-select', className, {
        'B-betslip-select-classic': classic,
        'B-active-up': openDirection === 'up',
        'B-active-down': openDirection === 'down',
    });
    return (
        <div className={containerClassName} onClick={toggleOpen} ref={selectRef}>
            <p className="B-bet-sel-drop-title">
                {(showSelected && selected?.name) || placeholder || ''}
                {showArrow && <i className={`global-icon ${openDirection ? 'icon-arrow-up4' : 'icon-arrow-down5'}`} />}
            </p>

            {!!options.length && <ul className="B-options">
                {options.map(item => <li key={item.value as Key}
                    onClick={e => select(e, item)}
                    className={selected === item ? 'B-active' : ''}>
                    {typeof item.name ==="string" ? t(item.name) : item.name}
                </li>)}
            </ul>}
        </div>
    );
};

export default Select;
